import { useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useVisibleAdminTabs } from './useVisibleAdminTabs';
import type { AdminCategory, AdminTab } from './adminTabs.config';

export interface ActiveAdminTab {
  /** `value` of the active tool, or `false` at the bare `/admin` landing. */
  activeValue: string | false;
  /** Category the active tool belongs to (null when none is active). */
  category: AdminCategory | null;
  /** The active tool's tab definition (null when none is active). */
  tab: AdminTab | null;
}

/**
 * Resolves the admin tool the current URL points at, plus its category.
 *
 * Shared by `AdminSideMenu` (to highlight the active row) and `AdminLayout`
 * (to build the breadcrumb) so the `matchPath` logic lives in one place.
 *
 * Matches against role-allowed tabs (including hidden ones) so a direct nav to
 * a hidden admin route resolves to no selection rather than mis-highlighting a
 * tool; the bare `/admin` landing resolves to none as well.
 */
export function useActiveAdminTab(): ActiveAdminTab {
  const location = useLocation();
  const { visibleTabs, roleTabs, groups } = useVisibleAdminTabs();

  return useMemo(() => {
    const matched = roleTabs.find((tab) =>
      matchPath({ path: tab.matchPattern }, location.pathname),
    );
    const activeValue =
      matched && visibleTabs.some((tab) => tab.value === matched.value)
        ? matched.value
        : false;
    if (!activeValue) return { activeValue: false, category: null, tab: null };

    for (const group of groups) {
      const tab = group.tabs.find((t) => t.value === activeValue);
      if (tab) return { activeValue, category: group.category, tab };
    }
    return { activeValue, category: null, tab: null };
  }, [location.pathname, roleTabs, visibleTabs, groups]);
}
